import { createSelector } from "reselect";

import * as R from "ramda";

import { getters } from "Portal/NewPortal/FollowupForm/model";

import { user as getUser } from "redux/modules/user/selectors";

import {
  WELCOME_COLORS,
  LOGIN_COLORS,
  GROUP_COLORS,
  CONTACT_COLORS
} from "Portal/NewPortal/FollowupForm/constants";

import isValid from "utils/value-types/validations/is-valid";

import isEmpty from "utils/value-types/is-empty";

import Helpers from "utils/Global/Helpers";

import * as STANDARD_MODULE_FIELD_IDS from "@sessionboard/sessionboard-value-types/src/constants/standard-module-field-ids";
import { resolveFontCss } from "utils/resolve-font-css";

export const getIsEditing = createSelector(getters.isSubmitted, isSubmitted => {
  return isSubmitted;
});

export const getIsEditingAndEditsNotAllowed = createSelector(
  getIsEditing,
  getters.allowEdits,
  getters.markCompletedAsViewOnly,
  (isEditing, allowEdits, markCompletedAsViewOnly) => {
    if (isEditing && markCompletedAsViewOnly) {
      return true;
    }
    return isEditing && !allowEdits ? true : false;
  }
);

export const getWelcomeColor = createSelector(
  getters.activeIndex,
  activeIndex => WELCOME_COLORS[activeIndex]
);

export const getWelcomeData = createSelector(getters.formData, formData =>
  R.isEmpty(formData) ? {} : formData.sections[0]
);

export const getLoginColor = createSelector(
  getters.activeIndex,
  activeIndex => LOGIN_COLORS[activeIndex]
);

export const getGroupColor = createSelector(
  getters.activeIndex,
  activeIndex => GROUP_COLORS[activeIndex]
);

export const getGroupData = createSelector(getters.formData, formData =>
  R.isEmpty(formData) ? {} : formData.sections[2]
);

export const getLogoImageUrl = createSelector(
  getters.formData,
  R.prop("logo_image_url")
);

export const getPageTitle = createSelector(getters.formData, R.prop("title"));

const isRequiredFieldValid = (value, emailId) => {
  if (R.isEmpty(R.dissoc("id", value))) {
    return true;
  }
  const type = R.propOr("text", "type", value);
  if (value.id === emailId) {
    return Helpers.isValidEmail(R.propOr("", "value", value));
  }

  return isValid(value, type);
};

const isFieldValid = (value, emailId) => {
  if (isEmpty(value, value.type)) {
    return true;
  }

  return isRequiredFieldValid(value, emailId);
};

export const getContactColor = createSelector(
  getters.activeIndex,
  activeIndex => CONTACT_COLORS[activeIndex]
);

export const getContactData = createSelector(getters.formData, formData =>
  R.isEmpty(formData) ? {} : formData.sections[1]
);

export const getContactDataFields = createSelector(
  getContactData,
  getters.contactValues,
  getters.isClosed,
  getters.previewing,
  getIsEditingAndEditsNotAllowed,
  (
    contactData,
    contactValues,
    isClosed,
    previewing,
    isEditingAndEditsNotAllowed
  ) => {
    return R.compose(
      R.map(field => {
        const fieldValue = R.propOr({}, field.id, contactValues);
        return {
          ...field,
          disabled: isEditingAndEditsNotAllowed || isClosed || previewing,
          isValid: !field.required
            ? isFieldValid(
                { ...fieldValue, id: field.id },
                STANDARD_MODULE_FIELD_IDS.CONTACTS.EMAIL
              )
            : isRequiredFieldValid(
                { ...fieldValue, id: field.id },
                STANDARD_MODULE_FIELD_IDS.CONTACTS.EMAIL,
                contactValues
              )
        };
      }),
      R.propOr([], "fields")
    )(contactData);
  }
);

export const getContactDataFieldsFiltered = createSelector(
  getContactDataFields,
  R.filter(
    ({ id }) =>
      ![
        STANDARD_MODULE_FIELD_IDS.CONTACTS.FIRST_NAME,
        STANDARD_MODULE_FIELD_IDS.CONTACTS.LAST_NAME,
        STANDARD_MODULE_FIELD_IDS.CONTACTS.EMAIL
      ].includes(id)
  )
);

export const getContactFirstLastNameFields = createSelector(
  getContactDataFields,
  R.compose(
    R.sortBy(({ id }) =>
      R.prop(id)({
        [STANDARD_MODULE_FIELD_IDS.CONTACTS.FIRST_NAME]: 0,
        [STANDARD_MODULE_FIELD_IDS.CONTACTS.LAST_NAME]: 1,
        [STANDARD_MODULE_FIELD_IDS.CONTACTS.EMAIL]: 2
      })
    ),
    R.filter(({ id }) =>
      [
        STANDARD_MODULE_FIELD_IDS.CONTACTS.FIRST_NAME,
        STANDARD_MODULE_FIELD_IDS.CONTACTS.LAST_NAME,
        STANDARD_MODULE_FIELD_IDS.CONTACTS.EMAIL
      ].includes(id)
    )
  )
);

export const getNextButtonText = createSelector(
  getters.activeIndex,
  activeIndex => {
    if (activeIndex === 0) {
      return "Continue";
    } else if (activeIndex === 1) {
      return "Next Step";
    }
    return "Submit";
  }
);

export const applyRules = (field, fields, values) => {
  const allRulesResults = R.map(rule => {
    const operator = R.prop("operator", rule);
    const conditionField = R.find(
      f => R.prop("form_field_id", f) === R.prop("condition_field_id", rule),
      fields
    );
    const conditionType = R.prop("type", conditionField);

    let actualConditionValue;
    if (conditionType === "dropdown") {
      actualConditionValue = R.pathOr(
        [],
        [conditionField.id, "value", "records"],
        values
      );
    } else {
      actualConditionValue = [R.pathOr([], [conditionField.id], values)];
    }
    const ruleValues = R.pathOr([], ["condition", "values"], rule);

    const ruleValuesResults = R.map(v => {
      const valExists = R.find(av => {
        const condVal = R.prop("value", av) || false;
        const condId = R.prop("id", av);
        const ruleVal =
          conditionType === "checkbox"
            ? JSON.parse(R.prop("value", v))
            : R.prop("value", v);

        return condVal === ruleVal || condId === ruleVal;
      }, actualConditionValue);

      // check if we have transitive conditional logic
      const isExitsTranstiveConditionalField = applyRules(
        conditionField,
        fields,
        values
      );

      // first time  + user select undefined value on the dropdown
      if (
        (valExists === undefined && R.length(actualConditionValue) === 0) ||
        (valExists === undefined && R.isNil(actualConditionValue[0])) ||
        !isExitsTranstiveConditionalField
      ) {
        return false;
      }
      if (operator === "IS" && !valExists) {
        return false;
      } else return !(operator === "IS_NOT" && valExists);
    }, ruleValues);

    return ruleValuesResults.includes(true);
  }, R.propOr([], "rules", field));
  const renderField =
    R.length(allRulesResults) > 0 ? allRulesResults.includes(true) : true;

  return renderField;
};

const getContactValuesValid = createSelector(
  getContactDataFields,
  getters.contactValues,
  (fields, contactValues) => {
    if (R.isEmpty(fields)) {
      return true;
    }

    return R.compose(
      R.all(field => {
        const renderField = applyRules(
          R.find(({ id }) => field.id === id, fields),
          fields,
          contactValues
        );

        if (!renderField) {
          return true;
        }

        if (!field.required) {
          if (R.isEmpty(R.dissoc("id", field)) || isEmpty(field, field.type)) {
            return true;
          }
        }
        const type = R.propOr("text", "type", field);
        if (field.id === STANDARD_MODULE_FIELD_IDS.CONTACTS.EMAIL) {
          return Helpers.isValidEmail(R.propOr("", "value", field));
        }
        return isValid(field, type);
      }),
      R.map(({ id, required }) => ({
        id,
        ...R.propOr({}, id, contactValues),
        required
      }))
    )(fields);
  }
);

export const getGroupValues = createSelector(
  getters.groupValues,
  R.filter(data => !R.isEmpty(R.propOr("", "value", data)))
);

export const getContactValues = createSelector(
  getters.contactValues,
  R.filter(data => !R.isEmpty(R.propOr("", "value", data)))
);

export const getNextButtonDisabled = createSelector(
  getters.activeIndex,
  getContactValuesValid,
  getters.isClosed,
  getters.previewing,
  getters.loading,
  getters.emailError,
  getIsEditingAndEditsNotAllowed,
  (
    activeIndex,
    contactValuesValid,
    isClosed,
    previewing,
    isloading,
    emailError,
    isEditingAndEditsNotAllowed
  ) => {
    if (emailError.length) return true;
    if (isloading) return true;
    if (isEditingAndEditsNotAllowed) return true;
    if (activeIndex === 0) {
      return false;
    } else if (activeIndex === 1) {
      return false;
    } else if (activeIndex === 2) {
      return previewing || isClosed ? true : !contactValuesValid;
    }
    return false;
  }
);

export const getShowBackButton = createSelector(
  getters.activeIndex,
  activeIndex => activeIndex > 0
);

export const getPortalUrl = createSelector(
  getters.formData,
  data => `/portals/${data.event_slug}/${data.event_uuid}`
);

export const getUserName = createSelector(getUser, user => {
  const name = [user.fname, user.lname].filter(v => v).join(" ");
  if (name.length) {
    return name;
  }
  return user.email;
});

export const getCssCode = createSelector(getters.styles, styles => {
  let css = "";

  const fonts = R.compose(
    R.uniq,
    R.filter(f => f)
  )([styles.portal_name_font, styles.section_header_font]);

  const fontCss = resolveFontCss({ fonts });
  if (fontCss && fontCss.length) {
    css = `${fontCss}`;
  }

  // accent
  css = `${css} 
  /** accentable primary button */
  .accentable-primary-button {
    background-color: ${styles.accent_color};
    border-color: ${styles.accent_color};
  }
  .accentable-primary-button:hover {
    background-color: ${styles.accent_color};
    border-color: ${styles.accent_color};
    filter: brightness(85%);
  }

  /** accentable tab */
  .accentable-tab:hover {
    color: ${styles.accent_color};
  }
  .accentable-tab.active-tab {
    border-color: ${styles.accent_color};
    color: ${styles.accent_color};
  }

  /** accentable generic hover */
  .accentable-hover:hover {
      border: 1px solid ${styles.accent_color};
  }
  /** accentable generic border */
  .accentable-border-with-hover  {
      border: 1px solid ${styles.accent_color};
  }
  .accentable-border-with-hover:hover  {
      border: 1px solid ${styles.accent_color};
  }


  /** accentable text */
  .accentable-text {
      color: ${styles.accent_color};
  }

      
`;

  if (styles.css_code && styles.css_code.length) {
    css = `${css} 
  
  ${styles.css_code}`;
  }

  return css;
});
